import { useEffect, useMemo, useState } from "react";
import Button from "../Button";
import DataTable from "./DataTable";

function CrudTable({
  columns = [],
  rows = [],
  isLoading = false,
  pagination,
  sortConfig,
  filters = {},
  onFiltersChange,
  onSort,
  onPageChange,
  searchKey = "search",
  pageKey = "page",
  searchDebounceMs = 400,
  onEdit,
  onDelete,
  renderActions,
  showActions = true,
  withTableFeatures = true,
  searchPlaceholder = "Search records...",
  noDataMessage = "No records found",
  tableScrollHeight = "52vh",
  enableFieldSearch = false,
  fieldSearchColumns = [],
  fieldSearchPlaceholder = "Search in field...",
  enableDateFilter = false,
  dateFilterColumnKey = "",
  dateFilterLabel = "Date range",
  enableDropdownFilters = false,
  dropdownFilters = [],
  searchable = true,
  sortable = true,
  paginated = true,
  toolbarFilters,
  toolbarAction,
  renderMobileCard,
  expandable = false,
  expandedRows = new Set(),
  renderExpandableContent = null,
  onRowClick = null,
  rowKeyField = "id",
}) {
  const isSearchEnabled = withTableFeatures && searchable;
  const isSortEnabled = withTableFeatures && sortable;
  const isPaginationEnabled = withTableFeatures && paginated;

  const [searchInput, setSearchInput] = useState(
    isSearchEnabled ? filters?.[searchKey] || "" : ""
  );
  const isControlledFilters = isSearchEnabled && typeof onFiltersChange === "function";

  useEffect(() => {
    if (!isControlledFilters) return;
    setSearchInput(filters?.[searchKey] || "");
  }, [filters, isControlledFilters, searchKey]);

  useEffect(() => {
    if (!isControlledFilters) return;

    const timer = setTimeout(() => {
      const nextSearch = String(searchInput || "");
      if (nextSearch === (filters?.[searchKey] || "")) return;

      onFiltersChange({
        [searchKey]: nextSearch,
        [pageKey]: 1,
      });
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [
    filters,
    isControlledFilters,
    onFiltersChange,
    pageKey,
    searchDebounceMs,
    searchInput,
    searchKey,
  ]);

  const resolvedColumns = useMemo(() => {
    const tableColumns = columns.map((column) => ({
      ...column,
      sortable: isSortEnabled && Boolean(column.sortable),
      render: column.render ? (item, index) => column.render(item, index) : undefined,
    }));

    if (!showActions) {
      return tableColumns;
    }

    return [
      ...tableColumns,
      {
        key: "actions",
        label: "Actions",
        render: (item) => (
          <div className="flex items-center gap-1.5">
            {renderActions && renderActions(item)}
            {onEdit && (
              <Button
                variant="secondary"
                size="sm"
                className="px-2.5"
                onClick={() => onEdit(item)}
              >
                <svg className="mr-1 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </Button>
            )}
            {onDelete && (
              <Button
                variant="danger"
                size="sm"
                className="px-2.5"
                onClick={() => onDelete(item)}
              >
                <svg className="mr-1 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </Button>
            )}
          </div>
        ),
      },
    ];
  }, [columns, onDelete, onEdit, isSortEnabled, renderActions, showActions]);

  return (
    <DataTable
      columns={resolvedColumns}
      data={rows}
      isLoading={isLoading}
      withTableFeatures={withTableFeatures}

      /* Pagination */
      pagination={isPaginationEnabled ? pagination : undefined}
      onPageChange={isPaginationEnabled ? onPageChange : undefined}

      /* Sorting */
      onSort={isSortEnabled ? onSort : undefined}
      sortConfig={isSortEnabled ? sortConfig : undefined}

      /* Search */
      searchValue={isSearchEnabled ? searchInput : undefined}
      onSearchChange={isSearchEnabled ? setSearchInput : undefined}
      searchPlaceholder={isSearchEnabled ? searchPlaceholder : undefined}
      noDataMessage={noDataMessage}

      // scrollHeight={tableScrollHeight}

      /* Extra Filters */
      enableFieldSearch={isSearchEnabled && enableFieldSearch}
      fieldSearchColumns={isSearchEnabled ? fieldSearchColumns : []}
      fieldSearchPlaceholder={fieldSearchPlaceholder}

      enableDateFilter={isSearchEnabled && enableDateFilter}
      dateFilterColumnKey={dateFilterColumnKey}
      dateFilterLabel={dateFilterLabel}

      enableDropdownFilters={isSearchEnabled && enableDropdownFilters}
      dropdownFilters={isSearchEnabled ? dropdownFilters : []}

      /* Final Flags */
      searchable={isSearchEnabled}
      sortable={isSortEnabled}
      paginated={isPaginationEnabled}
      toolbarFilters={toolbarFilters}
      toolbarAction={toolbarAction}
      renderMobileCard={renderMobileCard}

      /* Expandable */
      expandable={expandable}
      expandedRows={expandedRows}
      renderExpandableContent={renderExpandableContent}
      onRowClick={onRowClick}
      rowKeyField={rowKeyField}
    />
  );
}

export default CrudTable;
